import React, { FC } from 'react';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import {
  GetRewardsConfigResponse,
  maxWinPrizeForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { BonusEntriesBadge } from 'Rewards/components/BonusEntriesBadge';
import styles from './styles.module.css';

export type ScratchOffCardProps = {
  status: GetAgentStatusResponse | null;
  config: GetRewardsConfigResponse | null;
  loading: boolean;
  /** Called when producer clicks the CTA to navigate to the full rewards page. */
  onCtaClick: () => void;
};

export const ScratchOffCard: FC<ScratchOffCardProps> = ({
  status,
  config,
  loading,
  onCtaClick,
}) => {
  if (loading) {
    return (
      <div className={styles.card} data-testid="scratch-off-card">
        <p className={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  const currentPhaseCode = status?.phase?.code ?? config?.phase?.code ?? null;
  const maxPrize =
    config && currentPhaseCode
      ? maxWinPrizeForPhase(config, currentPhaseCode)
      : null;

  const regularEntries = status?.balance?.regular ?? 0;
  const bonusEntries = status?.balance?.bonus ?? 0;
  const policiesRemaining = status?.progress?.policiesUntilNextEntry ?? 0;
  const entryThreshold = config?.config?.program?.entry_threshold ?? null;
  const filledSegments =
    entryThreshold != null
      ? Math.max(0, entryThreshold - policiesRemaining)
      : 0;

  return (
    <div className={styles.card} data-testid="scratch-off-card">
      <div className={styles.cardHeader}>
        <p className={styles.sectionLabel}>SCRATCH-OFF CARDS</p>
      </div>

      {maxPrize != null && (
        <p className={styles.prizeHeadline} data-testid="scratch-off-headline">
          Win up to ${maxPrize.toLocaleString('en-US')}
        </p>
      )}

      <div className={styles.entriesRow} data-testid="scratch-off-counts">
        <span
          className={styles.entriesCount}
          data-testid="scratch-off-regular-count"
        >
          {regularEntries}
        </span>
        <span className={styles.entriesLabel}>
          {regularEntries === 1 ? 'ENTRY' : 'ENTRIES'} AVAILABLE
        </span>
        <BonusEntriesBadge
          phaseNumber={status?.phase?.number ?? 1}
          bonusCount={bonusEntries}
        />
      </div>

      {status?.progress && entryThreshold != null && (
        <div
          className={styles.progressSection}
          data-testid="scratch-off-progress"
        >
          <div className={styles.progressLabels}>
            <span>Next entry</span>
            <span>
              {policiesRemaining} {policiesRemaining === 1 ? 'SALE' : 'SALES'}{' '}
              AWAY
            </span>
          </div>
          <div className={styles.progressBar}>
            {Array.from({ length: entryThreshold }).map((_, i) => (
              <div
                key={i}
                className={
                  i < filledSegments
                    ? styles.progressSegmentFilled
                    : styles.progressSegmentEmpty
                }
              />
            ))}
          </div>
        </div>
      )}

      <button
        className={styles.ctaLink}
        onClick={onCtaClick}
        data-testid="scratch-off-cta"
      >
        View full rewards page to play
      </button>
    </div>
  );
};
